import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShieldCheck, Truck, RotateCcw, Star } from "lucide-react";
import { api, imageUrl, formatApiError } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { ProductImagePlaceholder } from "@/components/site/Logo";
import ProductCard, { Stars } from "@/components/site/ProductCard";
import { Reveal } from "@/components/site/Reveal";
import { toast } from "sonner";

function ReviewForm({ productId, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/reviews", { product_id: productId, rating, display_name: name, text });
      toast.success("Thank you — your review has been submitted for moderation.");
      setName(""); setText(""); setRating(5);
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSending(false);
  };

  return (
    <form onSubmit={submit} className="gold-hairline bg-[#0D0D0D] p-6 space-y-4" data-testid="review-form">
      <h4 className="font-serif-display text-xl text-white">Share your experience</h4>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <button type="button" key={i} onClick={() => setRating(i)} aria-label={`Rate ${i} stars`} data-testid={`review-star-${i}`}>
            <Star size={20} className={i <= rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#3a3a3a]"} />
          </button>
        ))}
      </div>
      <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name"
        className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37]"
        data-testid="review-name-input" />
      <textarea required minLength={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Your review" rows={4}
        className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37] resize-none"
        data-testid="review-text-input" />
      <button type="submit" className="btn-gold" disabled={sending} data-testid="review-submit-button">
        {sending ? "Submitting..." : "Submit Review"}
      </button>
    </form>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { addToCart } = useShop();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ average: 0, count: 0 });
  const [related, setRelated] = useState([]);
  const [sizeLabel, setSizeLabel] = useState("");
  const [qty, setQty] = useState(1);
  const [sort, setSort] = useState("newest");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null); setNotFound(false);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      let first = data.sizes.find((s) => s.stock > 0) || data.sizes[0];
      if (searchParams.get("format") === "rollon") {
        first = data.sizes.find((s) => s.label.toLowerCase().includes("roll") && s.stock > 0) || first;
      }
      setSizeLabel(first?.label || "");
      api.get("/products").then((r) => {
        setRelated(r.data.filter((p) => p.id !== data.id && p.category === data.category).slice(0, 4));
      });
    }).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    api.get("/reviews", { params: { product_id: id } }).then((r) => setReviews(r.data)).catch(() => {});
    api.get("/reviews/summary", { params: { product_id: id } }).then((r) => setSummary(r.data)).catch(() => {});
  }, [id]);

  const size = useMemo(() => product?.sizes.find((s) => s.label === sizeLabel), [product, sizeLabel]);
  const maxQty = size?.stock || 0;
  const isRollon = sizeLabel.toLowerCase().includes("roll");
  const currentImage = useMemo(() => {
    if (!product) return "";
    return isRollon ? (product.rollon_image_url || product.image_url || "") : (product.image_url || "");
  }, [product, isRollon]);

  const sortedReviews = useMemo(() => {
    const list = [...reviews];
    if (sort === "highest") list.sort((a, b) => b.rating - a.rating);
    else if (sort === "lowest") list.sort((a, b) => a.rating - b.rating);
    else list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [reviews, sort]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="product-not-found">
        <h1 className="font-serif-display text-4xl text-white">Fragrance not found</h1>
        <Link to="/shop" className="btn-gold mt-8 inline-flex">Back to Shop</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="pt-40 pb-32 max-w-7xl mx-auto px-6"><div className="aspect-[3/4] max-w-md bg-[#0D0D0D] animate-pulse" /></div>;
  }

  return (
    <div className="pt-20" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-12 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <Reveal>
            <div className="relative aspect-[3/4] overflow-hidden gold-hairline bg-[#0A0A0A]" data-testid="product-image">
              {currentImage ? (
                <motion.img
                  key={currentImage}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
                  src={imageUrl(currentImage)}
                  alt={`${product.name} — ${sizeLabel}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <ProductImagePlaceholder name={product.name} className="w-full h-full" />
              )}
              {product.sizes.length > 1 && (
                <span className="absolute top-4 left-4 bg-[#080808]/85 backdrop-blur border border-[#D4AF37]/40 text-[#D4AF37] font-sans-body text-[9px] font-bold uppercase tracking-[0.25em] px-3 py-1.5" data-testid="format-badge">
                  {sizeLabel}
                </span>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="flex flex-col">
            <p className="eyebrow">{product.category}</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-white font-light mt-3 leading-tight" data-testid="product-name">
              {product.name}
            </h1>
            {product.tagline && (
              <p className="font-serif-display italic text-xl text-[#C6A15B] mt-2" data-testid="product-tagline">{product.tagline}</p>
            )}
            <div className="mt-4 flex items-center gap-3">
              <Stars rating={summary.average} testid="product-detail-stars" />
              {summary.count > 0 && (
                <span className="font-sans-body text-xs text-[#B8B8B8]" data-testid="review-count">
                  {summary.average.toFixed(1)} · {summary.count} review{summary.count !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <p className="font-mono-price text-3xl text-[#D4AF37] mt-6" data-testid="product-price">
              ${(size?.price || 0).toFixed(2)}
            </p>

            {product.description && (
              <p className="font-sans-body text-sm text-[#B8B8B8] font-light leading-relaxed mt-6" data-testid="product-description">
                {product.description}
              </p>
            )}
            {product.occasion && (
              <p className="font-sans-body text-[11px] text-[#C6A15B] mt-5 uppercase tracking-[0.2em]" data-testid="product-occasion">
                Suggested wear — {product.occasion}
              </p>
            )}
            {product.notes && (
              <div className="mt-6 gold-hairline bg-[#0D0D0D] p-5" data-testid="product-notes">
                <p className="font-sans-body text-[10px] uppercase tracking-[0.3em] text-[#C6A15B] mb-2">Fragrance Notes</p>
                <p className="font-sans-body text-sm text-[#B8B8B8] font-light whitespace-pre-wrap">{product.notes}</p>
              </div>
            )}

            <div className="mt-8">
              <p className="font-sans-body text-[10px] uppercase tracking-[0.3em] text-[#C6A15B] mb-3">Choose Your Format</p>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s.label}
                    onClick={() => { setSizeLabel(s.label); setQty(1); }}
                    disabled={s.stock <= 0}
                    className={`px-5 py-3 border font-sans-body text-xs tracking-[0.1em] transition-colors ${
                      sizeLabel === s.label
                        ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-[#D4AF37]/20 text-[#B8B8B8] hover:border-[#D4AF37]/50"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                    data-testid={`size-option-${s.label.replace(/\s/g, "-")}`}
                  >
                    {s.label} — ${s.price.toFixed(2)}
                    {s.stock <= 0 && <span className="block text-[9px] text-red-400/80 mt-0.5">Sold out</span>}
                    {s.stock > 0 && s.stock <= 5 && <span className="block text-[9px] text-[#C6A15B] mt-0.5">Only {s.stock} left</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-stretch gap-4">
              <div className="flex items-center gold-hairline">
                <button className="px-4 py-3 text-[#B8B8B8] hover:text-[#D4AF37] disabled:opacity-40"
                  onClick={() => setQty(Math.max(1, qty - 1))} disabled={qty <= 1}
                  data-testid="quantity-minus" aria-label="Decrease quantity">
                  <Minus size={15} />
                </button>
                <span className="px-5 font-mono-price text-white" data-testid="quantity-value">{qty}</span>
                <button className="px-4 py-3 text-[#B8B8B8] hover:text-[#D4AF37] disabled:opacity-40"
                  onClick={() => setQty(Math.min(maxQty, qty + 1))} disabled={qty >= maxQty}
                  data-testid="quantity-plus" aria-label="Increase quantity">
                  <Plus size={15} />
                </button>
              </div>
              <button
                className="btn-gold flex-1"
                disabled={!size || size.stock <= 0}
                onClick={() => { addToCart(product, sizeLabel, qty); setQty(1); }}
                data-testid="product-detail-add-to-cart"
              >
                {size && size.stock <= 0 ? "Sold Out" : "Add to Bag"}
              </button>
            </div>

            <div className="mt-10 space-y-4 gold-hairline-t pt-8">
              <div className="flex items-start gap-3">
                <Truck size={18} className="text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="font-sans-body text-xs text-[#B8B8B8] font-light leading-relaxed">
                  Standard shipping $10.50 · Express shipping $15.00. Calculated at checkout.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <RotateCcw size={18} className="text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="font-sans-body text-xs text-[#B8B8B8] font-light leading-relaxed">
                  See our <Link to="/policies/returns" className="text-[#D4AF37] underline underline-offset-4">returns policy</Link> for details.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <ShieldCheck size={18} className="text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="font-sans-body text-xs text-[#B8B8B8] font-light leading-relaxed">
                  Secure checkout. Payments are processed by Stripe — we never see or store your card details.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* REVIEWS */}
        <div className="mt-24 gold-hairline-t pt-16" data-testid="product-reviews-section">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="font-serif-display text-3xl text-white font-light">Customer Reviews</h2>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="review-sort-select"
              className="bg-[#0D0D0D] border border-[#D4AF37]/20 text-[#B8B8B8] font-sans-body text-xs px-3 py-2.5 focus:outline-none focus:border-[#D4AF37] w-fit">
              <option value="newest">Newest</option>
              <option value="highest">Highest Rating</option>
              <option value="lowest">Lowest Rating</option>
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
            <div className="lg:col-span-2 space-y-6">
              {sortedReviews.length === 0 ? (
                <div className="gold-hairline bg-[#0D0D0D] p-10 text-center" data-testid="no-reviews-message">
                  <p className="font-serif-display text-2xl text-white">Be the first to leave a review.</p>
                  <p className="font-sans-body text-sm text-[#B8B8B8] font-light mt-3">
                    Share how this fragrance made its impression on you.
                  </p>
                </div>
              ) : (
                sortedReviews.map((r) => (
                  <motion.div key={r.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    className="gold-hairline bg-[#0D0D0D] p-6" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <Stars rating={r.rating} />
                      <span className="font-sans-body text-[11px] text-[#666666]">
                        {new Date(r.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                      </span>
                    </div>
                    <p className="font-sans-body text-sm text-white mt-3 leading-relaxed">{r.text}</p>
                    <p className="font-sans-body text-[11px] uppercase tracking-[0.2em] text-[#C6A15B] mt-4">
                      {r.display_name}
                      {r.verified_purchase && <span className="ml-2 text-[#D4AF37]">· Verified Purchase</span>}
                    </p>
                  </motion.div>
                ))
              )}
            </div>
            <ReviewForm productId={id} onSubmitted={() => {}} />
          </div>
        </div>

        {/* RELATED */}
        {related.length > 0 && (
          <div className="mt-24 gold-hairline-t pt-16" data-testid="related-products">
            <h2 className="font-serif-display text-3xl text-white font-light mb-10">You May Also Love</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
